<template>
  <div class="renewal-notifications" v-if="renewableItems.length > 0">
    <el-alert
      :title="`Renewals Expiring Soon`"
      type="warning"
      :closable="false"
      show-icon
      class="renewal-alert"
      @click.native="toggleExpanded"
    >
      <template slot="title">
        <div class="alert-header">
          <span>Renewal Available</span>
          <i :class="expanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" class="expand-icon"></i>
        </div>
      </template>
      <div class="alert-description">
        {{ renewableItems.length }} membership{{ renewableItems.length > 1 ? 's' : '' }} ready for renewal
      </div>
    </el-alert>

    <el-collapse-transition>
      <div v-if="expanded" class="renewal-items-container">
        <div class="renewal-items">
          <div
            v-for="item in renewableItems"
            :key="item._id"
            class="renewal-item"
            @click="navigateToRenewal(item)"
          >
            <div class="renewal-content">
              <div class="renewal-info">
                <div class="renewal-name">{{ item.regoEntity.name }}</div>
                <div class="renewal-expiry">
                  <i class="el-icon-time"></i>
                   {{ formatExpiryDate(item.expiry) }}
                </div>
              </div>
              <div class="renewal-action">
                <el-button type="warning" size="small" icon="el-icon-refresh" class="renewal-button">
                  <span class="button-text-full">Renew Now</span>
                  <span class="button-text-short">Renew</span>
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-collapse-transition>
  </div>
</template>

<script>
import moment from 'moment';
import _ from 'lodash';

export default {
  name: 'RenewalNotifications',
  data() {
    return {
      loading: false,
      expanded: false
    };
  },
  methods: {

    isRenewableInNext30Days(registration) {
      // Backend already handles expiry logic, just check if renewable
      return _.get(registration, 'regoEntity.isRenewable');
    },

    formatExpiryDate(expiry) {
      const expiryMoment = moment(expiry);
      const now = moment();

      if (expiryMoment.isBefore(now, 'day')) {
        return `Expired on ${expiryMoment.format('MMM DD, YYYY')}`;
      }
      return `Expires on ${expiryMoment.format('MMM DD, YYYY')}`;
    },

    async navigateToRenewal(item) {
      try {
        // Extract entityId and productId from the registration item
        const { entityId, productId } = item;

        if (!entityId || !productId) {
          console.error('Missing entityId or productId in registration:', item);
          this.$message.error('Unable to navigate to renewal page - missing required information');
          return;
        }

        // Navigate to finder with entityId and productId
        const pathName = this.hasCustomEntity ? 'custom-finder' : 'finder';

        this.$router.push({
          name: pathName,
          query: {
            entityId,
            productId
          }
        });
      } catch (error) {
        console.error('Error navigating to renewal:', error);
        this.$message.error('Unable to navigate to renewal page');
      }
    },

    toggleExpanded() {
      this.expanded = !this.expanded;
    }
  },

  computed: {
    memberRegistrations() {
      return this.$store.getters['user/getActiveMemberRegos'] || [];
    },

    renewableItems() {
      if (!this.memberRegistrations || this.memberRegistrations.length === 0) {
        return [];
      }

      // Filter for renewable items - backend already handles expiry logic
      const renewable = this.memberRegistrations.filter((registration) => this.isRenewableInNext30Days(registration));

      // Sort by expiry date (soonest first)
      return renewable.sort((a, b) => moment(a.expiry).valueOf() - moment(b.expiry).valueOf());
    },

    hasCustomEntity() {
      const customEntity = this.$store.getters['national/getEntity'];
      return customEntity && customEntity.level && customEntity.name;
    }
  }
};
</script>

<style lang="scss" scoped>
.renewal-notifications {
  margin: 20px 0;
  width: 100%;

  // Ensure it works well in main content area
  @media (min-width: 993px) {
    margin: 0;
    width: auto;
    display: inline-block;
  }

  .renewal-alert {
    width: 100%;
    cursor: pointer;

    @media (min-width: 993px) {
      min-width: fit-content;
    }

    .alert-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;

      .expand-icon {
        font-size: 14px;
        transition: transform 0.2s ease;
      }
    }

    .alert-description {
      font-size: 14px;
      margin-top: 4px;
    }
  }

  .renewal-items-container {
    border: 1px solid #f5c6cb;
    border-top: none;
    border-radius: 0 0 4px 4px;
    background: #fff;

    .renewal-items {
      max-height: 300px;
      overflow-y: auto;
      padding: 0;

      .renewal-item {
        border-bottom: 1px solid #f5f5f5;
        padding: 16px;
        cursor: pointer;
        transition: all 0.2s ease;

        &:last-child {
          border-bottom: none;
        }

        &:hover {
          background: #fef9e7;
        }

        .renewal-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
        }

        .renewal-info {
          flex: 1;

          .renewal-name {
            font-size: 16px;
            font-weight: 600;
            margin-bottom: 8px;
            color: #333;
          }

          .renewal-expiry {
            font-size: 13px;
            color: #666;
            display: flex;
            align-items: center;
            gap: 4px;

            i {
              font-size: 12px;
              color: #e6a23c;
            }
          }
        }

        .renewal-action {
          flex-shrink: 0;
        }
      }
    }
  }
}

// Desktop - show full text
.button-text-short {
  display: none;
}

.button-text-full {
  display: inline;
}

@media (max-width: 768px) {
  .renewal-notifications {
    margin: 16px 0;

    .renewal-items-container {
      .renewal-items {
        max-height: 250px;

        .renewal-item {
          padding: 12px;

          // Keep horizontal layout on mobile
          .renewal-content {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
          }

          .renewal-action {
            flex-shrink: 0;

            .renewal-button {
              .button-text-full {
                display: none;
              }

              .button-text-short {
                display: inline;
              }
            }
          }
        }
      }
    }
  }
}
</style>
